<script setup lang="ts">
/**
 * „Launcher“: a searchable, grouped entry page — three levels of navigation
 * over link tiles. Level 1 are groups (collapsible, optional icon), level 2
 * sections (collapsible), level 3 the items. First consumer is the ERP's
 * Stammdaten cockpit; a central settings hub (Verwaltung → ERP → topics) is
 * the next.
 *
 * The caller owns the data and hands it in already translated (`groups`,
 * `filters`); the launcher does search, filter, the open state of every level,
 * the hit counts and the settings dialog.
 *
 * Rules it carries, whatever the data:
 * - Search is a case-insensitive substring on the item's label (and its
 *   `keywords`); the match is marked in the label.
 * - A filter chip selects items by `tags`; „Alle“ lifts the filter.
 * - Levels without a hit drop out; while searching or filtering every
 *   surviving level opens and counts its hits.
 * - On arrival only the first group is open, no section (`initialOpen`).
 * - A group holding a single section with the group's own label renders its
 *   tiles directly — no inner accordion repeating the title.
 *
 * With `reorderable` the user puts the groups in their own order: a grip in
 * front of each group head (I-3239). The launcher only reports `reorder(ids)`;
 * the caller keeps the order and re-sorts `groups`.
 *
 * With `storageKey` the search text survives a return within the tab session
 * (`<key>.query` in sessionStorage) and „alles aufgeklappt“ is a setting of
 * the user (`shell.panes.<key>.expand_all`, the kit's settings contract,
 * I-2510).
 */
import { ChevronRightIcon, SettingsIcon } from '@blpde/nx-icons';
import GripVertical from 'lucide-vue-next/dist/esm/icons/grip-vertical.js';
import { computed, onMounted, ref, toRaw, useTemplateRef, watch } from 'vue';
import { RouterLink } from 'vue-router';
import NxBadge from '../Badge/NxBadge.vue';
import NxButton from '../Button/NxButton.vue';
import NxChip from '../Chip/NxChip.vue';
import NxDialog from '../Dialog/NxDialog.vue';
import NxSearchInput from '../Inputs/NxSearchInput.vue';
import NxSwitch from '../Inputs/NxSwitch.vue';
import { useNxPaneState } from '../Settings/settingsSource';
import { reorderItems } from '../Sortable/sortMath';
import { useNxSortable } from '../Sortable/useNxSortable';
import type {
  NxLauncherFilter,
  NxLauncherGroup,
  NxLauncherInitialOpen,
  NxLauncherItem,
  NxLauncherSection,
} from './types';

const props = withDefaults(
  defineProps<{
    groups: readonly NxLauncherGroup[];
    /** Filter chips; the „all“ chip in front of them is built in. None → no chip row. */
    filters?: readonly NxLauncherFilter[];
    /**
     * What the launcher remembers: the search text in the tab session
     * (`<key>.query`), „expand everything“ in the user's settings
     * (`shell.panes.<key>`). Without it the launcher remembers nothing.
     */
    storageKey?: string;
    /** Which groups are open on arrival. Default: the first one. */
    initialOpen?: NxLauncherInitialOpen;
    /**
     * The `initialOpen` groups arrive with their sections open too — for a page
     * opened on one group on purpose (the ERP menu's area), where a second click
     * per section would only be in the way. Default `false`.
     */
    openSectionsOnArrival?: boolean;
    /** Put the caret in the search field on mount. Default `true`. */
    autofocus?: boolean;
    /** Show the settings gear (with „expand everything“). Default `true`. */
    showSettings?: boolean;
    /** Placeholder (and accessible name) of the search field. */
    searchPlaceholder?: string;
    /** Accessible name of the filter chip row. */
    filtersLabel?: string;
    /** Caption of the built-in chip that lifts the filter. */
    allFilterLabel?: string;
    /** Title of the settings dialog, also the gear's tooltip. */
    settingsTitle?: string;
    settingsDescription?: string;
    /** Heading above the display settings in the dialog. */
    settingsDisplayLabel?: string;
    /** Caption of the „expand everything“ switch. */
    expandAllLabel?: string;
    /** Caption of the dialog's close button. */
    closeLabel?: string;
    /** Shown when nothing is left and no search text is set. */
    emptyText?: string;
    /** Shown when a search leaves nothing; gets the trimmed search text. */
    noHitsText?: (query: string) => string;
    /** Hit counter on a level while searching or filtering. */
    hitCountText?: (count: number) => string;
    /**
     * The user may put the groups in their own order: each group head gets a
     * grip in front — drag it, or Space/Alt+arrow on the focused grip — and the
     * launcher reports `reorder`. No grips while a search or filter runs: a hit
     * list is not the order. Default `false`.
     */
    reorderable?: boolean;
  }>(),
  {
    filters: () => [],
    storageKey: undefined,
    initialOpen: 'first',
    openSectionsOnArrival: false,
    autofocus: true,
    showSettings: true,
    searchPlaceholder: 'Suche',
    filtersLabel: 'Filter',
    allFilterLabel: 'Alle',
    settingsTitle: 'Ansichts-Einstellungen',
    settingsDescription: '',
    settingsDisplayLabel: 'Darstellung',
    expandAllLabel: 'Alle Bereiche aufgeklappt anzeigen',
    closeLabel: 'Schließen',
    emptyText: 'Keine Einträge gefunden',
    noHitsText: (query: string) => `Keine Einträge gefunden für „${query}“`,
    hitCountText: (count: number) => `${count} Treffer`,
    reorderable: false,
  }
);

/** The selected filter value; `null` is the „all“ chip. */
const filter = defineModel<string | null>('filter', { default: null });

const emit = defineEmits<{
  /** An item was clicked (a link still navigates on its own). */
  select: [item: NxLauncherItem];
  /**
   * `reorderable`: the ids of all `groups` in their new order. A group not
   * shown right now (no item) keeps its place.
   */
  reorder: [ids: string[]];
}>();

defineSlots<{
  /** Above the toolbar. */
  header?: () => unknown;
  /** Extra controls at the end of the toolbar, before the gear. */
  'toolbar-end'?: () => unknown;
  /** Extra content in the settings dialog, below the display settings. */
  settings?: () => unknown;
  /** Replaces the badge at the tile's trailing edge. */
  'item-trailing'?: (props: { item: NxLauncherItem }) => unknown;
}>();

// Web storage, guarded: private-mode browsers throw on access instead of
// returning null, and remembering must never be the reason the page fails.
const storageRead = (storage: () => Storage, key: string | undefined): string | null => {
  if (key === undefined) return null;
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
};

const storageWrite = (storage: () => Storage, key: string | undefined, value: string | null) => {
  if (key === undefined) return;
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {
    // Storage unavailable — the launcher works, it just cannot remember.
  }
};

const session = (): Storage => sessionStorage;

/**
 * The search text survives leaving and re-entering the page, and a reload with
 * it. `sessionStorage`, not `localStorage`, on purpose: a filter remembered for
 * days is one someone meets again without remembering they set it, and then
 * reads the missing entries as a defect. Within a tab session the connection to
 * one's own typing still holds.
 */
const queryKey = computed(() =>
  props.storageKey === undefined ? undefined : `${props.storageKey}.query`
);

const query = ref(storageRead(session, queryKey.value) ?? '');

watch(query, (next) => {
  // Removing rather than storing '' so a cleared field does not come back as a
  // remembered empty search on the next visit.
  storageWrite(session, queryKey.value, next === '' ? null : next);
});

// A launcher: typing is the primary action on arrival, so the caret starts in the
// search field rather than making the user click into it first.
const searchField = useTemplateRef<{ inputElement?: { focus(): void } | null }>('search-field');
onMounted(() => {
  if (props.autofocus) searchField.value?.inputElement?.focus();
});

/**
 * „Show everything expanded“ — a display preference of the user, so it lives
 * in their settings and follows the account (unlike the search text).
 * Re-meeting it cannot mislead: it reveals content rather than hiding it,
 * which is exactly why the search text gets the narrower session scope.
 */
const pane = useNxPaneState<{ expand_all: boolean }>(() => props.storageKey);

const expandAll = ref(pane.state.value.expand_all === true);
const isSettingsOpen = ref(false);

watch(
  () => pane.state.value.expand_all === true,
  (stored) => {
    if (stored !== expandAll.value) expandAll.value = stored;
  }
);
watch(expandAll, (next) => {
  if (next !== (pane.state.value.expand_all === true)) pane.patch({ expand_all: next });
});

interface VisibleSection {
  section: NxLauncherSection;
  items: NxLauncherItem[];
}

interface VisibleGroup {
  group: NxLauncherGroup;
  sections: VisibleSection[];
  /** Lone section carrying the group's own title → no inner accordion. */
  flat: boolean;
}

/**
 * Which accordions are open is OUR state, not the DOM's.
 *
 * The obvious alternative — binding `:open` to an expression like
 * `index === 0 || isFiltering` — is broken, and not subtly: Vue patches a prop
 * only when its value CHANGES between renders (`value` is the single
 * force-patched key, runtime-core). A `<details>` the user collapsed by hand
 * keeps the old vnode value, so an expression that stays `true` never patches it
 * back open. Collapse the first group, then search for something inside it: the
 * summary reports a hit that cannot be reached. With „expand everything“ on, the
 * binding is permanently `true` and the group can never be reopened at all.
 *
 * So the sets below are the single source of truth, and `onGroupToggle` /
 * `onSectionToggle` sync them back from the element after a real click. Every
 * change we want is then a genuine value change, which Vue does patch.
 *
 * Search, filter and „expand everything“ therefore do NOT bypass the sets as a
 * display override. An override that only changed what `:open` computes gave the
 * binding a value independent of the sets, and the two drifted apart the moment a
 * user collapsed something by hand: the binding stayed `true`, so the next
 * hand-OPEN reported the value the binding already carried and was
 * indistinguishable from a render echo. Lifting the override then collapsed a
 * group the user had just opened (measured in a browser, review of PR #1142).
 *
 * Instead an override WRITES into the sets and remembers what it opened itself
 * (`overrideOpened*`). Touching a level by hand claims it — the mark drops, and
 * the level survives the override being lifted. Lifting removes only what is
 * still marked, which is what makes the arrival state come back. A level the
 * user already had open is never marked, so it is never taken from them.
 */
const sectionKey = (groupId: string, sectionId: string): string => `${groupId}:${sectionId}`;

const initialGroups = (): string[] => {
  if (props.initialOpen === 'none') return [];
  if (props.initialOpen === 'first') return props.groups.slice(0, 1).map((group) => group.id);
  return [...props.initialOpen];
};

const initialSections = (): string[] => {
  if (!props.openSectionsOnArrival) return [];
  const open = new Set(initialGroups());
  return props.groups
    .filter((group) => open.has(group.id))
    .flatMap((group) => group.sections.map((section) => sectionKey(group.id, section.id)));
};

/** Arrival state: the `initialOpen` groups, no section (unless `openSectionsOnArrival`). */
const openGroups = ref(new Set<string>(initialGroups()));
const openSections = ref(new Set<string>(initialSections()));

/** Levels an override opened and the user has not claimed since. */
const overrideOpenedGroups = ref(new Set<string>());
const overrideOpenedSections = ref(new Set<string>());

const isGroupOpen = (groupId: string): boolean => openGroups.value.has(groupId);

const isSectionOpen = (groupId: string, sectionId: string): boolean =>
  openSections.value.has(sectionKey(groupId, sectionId));

/**
 * A `<details>` fires `toggle` whenever its `open` attribute changes — including
 * on the render that creates it, in a later task (measured, not assumed). Those
 * echoes report the state we already hold, so comparing against it is what
 * separates a real click from our own rendering. Without that check the first
 * group would add itself on arrival and unfold its sections, breaking the
 * arrival state.
 */
const onGroupToggle = (group: NxLauncherGroup, event: Event): void => {
  const nowOpen = (event.target as HTMLDetailsElement).open;
  if (nowOpen === isGroupOpen(group.id)) return;

  const groups = new Set(openGroups.value);
  if (nowOpen) groups.add(group.id);
  else groups.delete(group.id);
  openGroups.value = groups;

  // The user decided about this group, so an override may no longer take it back.
  const claimed = new Set(overrideOpenedGroups.value);
  claimed.delete(group.id);
  overrideOpenedGroups.value = claimed;

  // Opening a group by hand reveals its sections too — one click instead of two.
  // They are claimed along with it: the click owns them, so lifting an override
  // must not collapse them again and undo half of what the user just did.
  if (!nowOpen) return;
  const sections = new Set(openSections.value);
  const claimedSections = new Set(overrideOpenedSections.value);
  for (const section of group.sections) {
    const key = sectionKey(group.id, section.id);
    sections.add(key);
    claimedSections.delete(key);
  }
  openSections.value = sections;
  overrideOpenedSections.value = claimedSections;
};

const onSectionToggle = (groupId: string, sectionId: string, event: Event): void => {
  const nowOpen = (event.target as HTMLDetailsElement).open;
  if (nowOpen === isSectionOpen(groupId, sectionId)) return;

  const key = sectionKey(groupId, sectionId);
  const sections = new Set(openSections.value);
  if (nowOpen) sections.add(key);
  else sections.delete(key);
  openSections.value = sections;

  const claimed = new Set(overrideOpenedSections.value);
  claimed.delete(key);
  overrideOpenedSections.value = claimed;
};

const allItems = computed(() =>
  props.groups.flatMap((group) => group.sections.flatMap((section) => section.items))
);

/**
 * A filter nothing carries yet gets a chip, but not a selectable one: a chip
 * leading to an empty page reads as a broken screen. It enables itself once an
 * item arrives.
 */
const carriedTags = computed(() => new Set(allItems.value.flatMap((item) => item.tags ?? [])));

const filterChips = computed(() =>
  props.filters.map((chip) => ({
    ...chip,
    disabled: chip.disabled === true || !carriedTags.value.has(chip.value),
  }))
);

const trimmedQuery = computed(() => query.value.trim());

// Search AND filter: either one expands every surviving level, so hits are never
// hidden behind a collapsed accordion.
const isFiltering = computed(() => trimmedQuery.value !== '' || filter.value !== null);

const hasOverride = computed(() => expandAll.value || isFiltering.value);

// Case-insensitive substring on the caption the user actually reads (plus the
// hidden keywords) — never on an id.
const matchesQuery = (item: NxLauncherItem): boolean => {
  const needle = trimmedQuery.value.toLowerCase();
  if (needle === '') return true;
  return [item.label, ...(item.keywords ?? [])].some((text) => text.toLowerCase().includes(needle));
};

const matchesFilter = (item: NxLauncherItem): boolean =>
  filter.value === null || (item.tags ?? []).includes(filter.value);

interface LabelPart {
  text: string;
  match: boolean;
}

/**
 * Splits a label around the query so the match can be wrapped in `<mark>`.
 * Deliberately parts-and-nodes instead of building an HTML string for `v-html`:
 * the query is user input, and interpolating it into markup would be an
 * injection hole.
 *
 * The offsets come from the lowercased label but slice the original one, which
 * assumes lowercasing preserves length. That holds for every language the app
 * ships in; it breaks for characters that expand, e.g. Turkish `İ` becoming `i`
 * plus a combining dot. Revisit when a label can contain those.
 */
const labelParts = (label: string): LabelPart[] => {
  const needle = trimmedQuery.value.toLowerCase();
  if (needle === '') return [{ text: label, match: false }];

  const at = label.toLowerCase().indexOf(needle);
  if (at < 0) return [{ text: label, match: false }];

  return [
    { text: label.slice(0, at), match: false },
    { text: label.slice(at, at + needle.length), match: true },
    { text: label.slice(at + needle.length), match: false },
  ].filter((part) => part.text !== '');
};

/** Items surviving the current filter in a group, across all its sections. */
const hitsIn = (visible: VisibleGroup): number =>
  visible.sections.reduce((total, section) => total + section.items.length, 0);

// Both levels keep the caller's order. Empty sections and empty groups are
// dropped rather than rendered as dead headers — which is also what hides
// levels without a hit while a query is active.
const visibleGroups = computed<VisibleGroup[]>(() =>
  props.groups
    .map((group) => {
      const sections = group.sections
        .map((section) => ({
          section,
          items: section.items.filter((item) => matchesQuery(item) && matchesFilter(item)),
        }))
        .filter((visible) => visible.items.length > 0);

      // Compared on the labels, not the ids: the rule is about the title the user
      // reads, and translations decide whether the two coincide.
      const first = sections[0];
      const flat =
        sections.length === 1 && first !== undefined && first.section.label === group.label;

      return { group, sections, flat };
    })
    .filter((visible) => visible.sections.length > 0)
);

/**
 * An override opens every level that survives it — by writing into the sets, not
 * beside them. Only levels it opens ITSELF get marked, so one the user already
 * had open is never claimed back; lifting the override then removes exactly the
 * marked ones, which is what brings the arrival state back.
 *
 * `visibleGroups` is watched alongside the flag because refining a query brings
 * new levels into view and those have to open too. That also means a level the
 * user collapsed under one query reopens on the next keystroke: a new query is a
 * new search, and a hit counter over a level that cannot be reached is the worse
 * of the two.
 */
watch(
  [hasOverride, visibleGroups],
  ([override, visible]) => {
    if (override) {
      const groups = new Set(openGroups.value);
      const sections = new Set(openSections.value);
      const markedGroups = new Set(overrideOpenedGroups.value);
      const markedSections = new Set(overrideOpenedSections.value);

      for (const { group, sections: visibleSections } of visible) {
        if (!groups.has(group.id)) {
          groups.add(group.id);
          markedGroups.add(group.id);
        }
        for (const { section } of visibleSections) {
          const key = sectionKey(group.id, section.id);
          if (!sections.has(key)) {
            sections.add(key);
            markedSections.add(key);
          }
        }
      }

      openGroups.value = groups;
      openSections.value = sections;
      overrideOpenedGroups.value = markedGroups;
      overrideOpenedSections.value = markedSections;
      return;
    }

    if (overrideOpenedGroups.value.size === 0 && overrideOpenedSections.value.size === 0) return;

    const groups = new Set(openGroups.value);
    for (const id of overrideOpenedGroups.value) groups.delete(id);
    const sections = new Set(openSections.value);
    for (const key of overrideOpenedSections.value) sections.delete(key);

    openGroups.value = groups;
    openSections.value = sections;
    overrideOpenedGroups.value = new Set();
    overrideOpenedSections.value = new Set();
  },
  { immediate: true }
);

const rootEl = useTemplateRef<HTMLElement>('root');

const canReorder = computed(() => props.reorderable && !isFiltering.value);

const sortable = useNxSortable<VisibleGroup>({
  items: () => visibleGroups.value,
  container: rootEl,
  disabled: () => !canReorder.value,
  labelOf: (visible) => visible.group.label,
  onReorder: (from, to) => {
    const moved = reorderItems(visibleGroups.value, from, to).map((visible) => visible.group.id);
    const shown = new Set(moved);
    emit(
      'reorder',
      props.groups.map((group) => (shown.has(group.id) ? (moved.shift() ?? group.id) : group.id))
    );
  },
});

// A search typed in the middle of a move ends it: the grips leave with it.
watch(canReorder, (can) => {
  if (!can) sortable.cancel();
});

/** A router link, a plain link, or a button — a disabled item never navigates. */
const itemElement = (item: NxLauncherItem) => {
  if (!item.disabled && item.to !== undefined) return { is: RouterLink, attrs: { to: item.to } };
  if (!item.disabled && item.href !== undefined) return { is: 'a', attrs: { href: item.href } };
  return { is: 'button', attrs: { type: 'button', disabled: item.disabled === true } };
};
</script>

<template>
  <div ref="root" class="nx-launcher">
    <slot name="header" />

    <!-- One row: search and filter narrow the same list, so they belong side by
         side rather than stacked. -->
    <div class="nx-launcher__toolbar">
      <!-- Sized through a wrapper, NOT through a class on the component: `NxInput`
           sets `inheritAttrs: false` and spreads `$attrs` onto the inner `<input>`,
           so a class handed to `NxSearchInput` travels past the field's own wrapper
           and lands on the input itself. Its wrapper is `width: 100%`, so this div
           is what decides the width. -->
      <div class="nx-launcher__search">
        <NxSearchInput ref="search-field" v-model="query" :placeholder="searchPlaceholder" />
      </div>

      <!-- Mutually exclusive, so exactly one chip is selected at any time and „all“
           is the way back, not an extra state. -->
      <div
        v-if="filterChips.length > 0"
        class="nx-launcher__filters"
        role="group"
        :aria-label="filtersLabel"
      >
        <NxChip class="nx-launcher__filter-chip" :selected="filter === null" @click="filter = null">
          {{ allFilterLabel }}
        </NxChip>
        <NxChip
          v-for="chip in filterChips"
          :key="chip.value"
          class="nx-launcher__filter-chip"
          :selected="filter === chip.value"
          :disabled="chip.disabled"
          @click="filter = chip.value"
        >
          {{ chip.label }}
        </NxChip>
      </div>

      <slot name="toolbar-end" />

      <!-- A gear without a context menu: NxDialog's trigger slot opens the dialog on
           the click itself. Last in the row on purpose — `margin-left: auto` only
           reaches the trailing edge if nothing follows it, otherwise it pushes the
           chips out there instead. -->
      <NxDialog
        v-if="showSettings"
        v-model:open="isSettingsOpen"
        class="nx-launcher__settings"
        :title="settingsTitle"
        :description="settingsDescription || undefined"
      >
        <template #trigger>
          <NxButton variant="ghost" height="s" :title="settingsTitle" :aria-label="settingsTitle">
            <SettingsIcon class="nxt-icon-lg" />
          </NxButton>
        </template>

        <template #content>
          <div class="nx-launcher__settings-body">
            <section>
              <h3 class="nx-launcher__settings-group">{{ settingsDisplayLabel }}</h3>
              <label class="nx-launcher__switch-row">
                <NxSwitch v-model="expandAll" height="xs" />
                <span>{{ expandAllLabel }}</span>
              </label>
            </section>
            <slot name="settings" />
          </div>
        </template>

        <template #buttons="{ close }">
          <NxButton @click="close">{{ closeLabel }}</NxButton>
        </template>
      </NxDialog>
    </div>

    <!-- Levels without a hit drop out, so a query that matches nothing leaves the
         toolbar over blank space. Naming the query back is what tells a user it was
         the search and not a broken screen — especially when the search text was
         restored from the previous visit. -->
    <p v-if="visibleGroups.length === 0" class="nx-launcher__empty">
      {{ trimmedQuery === '' ? emptyText : noHitsText(trimmedQuery) }}
    </p>

    <details
      v-for="(visible, index) in visibleGroups"
      :key="visible.group.id"
      class="nx-launcher__group"
      :data-group-id="visible.group.id"
      :open="isGroupOpen(visible.group.id)"
      v-bind="canReorder ? sortable.itemProps(index) : {}"
      @toggle="onGroupToggle(visible.group, $event)"
    >
      <summary class="nx-launcher__group-head">
        <!-- In the head, so it shows on a closed group too. A button inside a
             summary does not fold the group on its own; the prevented click
             keeps it that way for every engine. -->
        <button
          v-if="canReorder"
          type="button"
          class="nx-launcher__grip"
          v-bind="sortable.handleProps(index)"
          @click.prevent
        >
          <GripVertical aria-hidden="true" />
        </button>
        <ChevronRightIcon class="nx-launcher__disclosure nxt-icon-md" :filled="true" />
        <!-- `toRaw`: a caller keeping `groups` in a `ref` hands the icon in as a
             reactive proxy, which Vue warns about when it becomes a vnode type. -->
        <component
          :is="toRaw(visible.group.icon)"
          v-if="visible.group.icon"
          class="nx-launcher__group-icon nxt-icon-lg"
        />
        {{ visible.group.label }}
        <span v-if="isFiltering" class="nx-launcher__count">
          {{ hitCountText(hitsIn(visible)) }}
        </span>
      </summary>

      <div class="nx-launcher__group-body">
        <!-- A lone section carrying the group's own title gets no accordion — the
             tiles sit directly in the group. The grid itself is identical either
             way, so only the wrapper element switches instead of the whole subtree
             being written twice. -->
        <component
          :is="visible.flat ? 'div' : 'details'"
          v-for="{ section, items } in visible.sections"
          :key="section.id"
          :class="visible.flat ? undefined : 'nx-launcher__section'"
          :open="visible.flat ? undefined : isSectionOpen(visible.group.id, section.id)"
          @toggle="visible.flat ? undefined : onSectionToggle(visible.group.id, section.id, $event)"
        >
          <summary v-if="!visible.flat" class="nx-launcher__section-head">
            <ChevronRightIcon class="nx-launcher__disclosure nxt-icon-xs" :filled="true" />
            {{ section.label }}
            <span v-if="isFiltering" class="nx-launcher__count">
              {{ hitCountText(items.length) }}
            </span>
          </summary>

          <ul class="nx-launcher__items">
            <li v-for="item in items" :key="item.id">
              <component
                :is="itemElement(item).is"
                v-bind="itemElement(item).attrs"
                class="nx-launcher__item"
                :class="{ 'is-disabled': item.disabled }"
                @click="emit('select', item)"
              >
                <span class="nx-launcher__item-label">
                  <span v-for="(part, partIndex) in labelParts(item.label)" :key="partIndex">
                    <mark v-if="part.match">{{ part.text }}</mark>
                    <template v-else>{{ part.text }}</template>
                  </span>
                </span>

                <!-- Short badge with its long form as the title: spelling the long
                     form out on every tile would compete with the caption for the
                     eye without adding information. -->
                <slot name="item-trailing" :item="item">
                  <NxBadge
                    v-if="item.badge"
                    class="nx-launcher__item-badge"
                    size="xs"
                    :title="item.badgeTitle"
                  >
                    {{ item.badge }}
                  </NxBadge>
                </slot>
              </component>
            </li>
          </ul>
        </component>
      </div>
    </details>
  </div>
</template>

<style scoped>
/* The scroll owner: a host that bounds the launcher's height (a flex column with
   `overflow: hidden`) delegates scrolling to it.
   No horizontal padding on purpose: the host page already insets its content, and
   an own inset on top of it pushes the search field and the group cards visibly
   further in than the page title. Vertical padding only. */
.nx-launcher {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-md);
  /* No top padding: the sticky toolbar carries it, see below. */
  padding: 0 0 var(--nxt-space-md);
}

/* A bounded flex column, so its children must NOT shrink: the default
   `flex-shrink: 1` squeezes the groups until they fit instead of overflowing, and
   the group's own `overflow: hidden` then clips what is left — nothing overflows,
   so nothing scrolls and the content simply disappears. */
.nx-launcher > * {
  flex-shrink: 0;
}

/* Search field and chips in one row, both vertically centred. The row wraps
   instead of squeezing the field: below roughly 34rem the chips move to a second
   line rather than shrinking the input past legibility.

   Sticky, so search, filters and the settings gear stay reachable however far the
   groups below are scrolled. It carries the launcher's top padding itself (instead
   of the scroll owner) so the stuck row keeps the same breathing room above the
   field; the opaque background hides the cards scrolling underneath. */
.nx-launcher__toolbar {
  position: sticky;
  top: 0;
  z-index: var(--nxt-z-sticky);
  padding-top: var(--nxt-space-md);
  padding-bottom: var(--nxt-space-xs);
  margin-bottom: calc(var(--nxt-space-xs) * -1);
  background: var(--nxt-surface-bg);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-sm) var(--nxt-space-md);
}

/* Sits where the first group card would be, in the same rhythm as the cards. */
.nx-launcher__empty {
  margin: 0;
  padding: var(--nxt-space-lg);
  color: var(--nxt-fg-subtle);
  text-align: center;
  background: var(--nxt-surface-raised-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
}

/* Takes the free space up to a cap. The cap is not cosmetic: without it the field
   would eat the whole row on a wide monitor and push the chips to the far edge,
   with a lot of empty input between the two. */
.nx-launcher__search {
  flex: 1 1 24rem;
  max-width: calc(var(--nxt-size-unit) * 44);
}

.nx-launcher__filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-xs);
}

/* Pushed to the trailing edge, flush with the group cards below: the gear is a
   rarely used escape hatch, not part of the filter row it sits next to.
   `margin-left: auto` rather than a spacer element, so it falls in line with the
   chips when the toolbar wraps.
   `:deep` is required, not decorative: NxDialog renders TWO root nodes (the
   trigger div and the modal), and Vue skips the scope attribute on a fragment
   root. The class arrives through `v-bind="$attrs"`, but a plain scoped rule would
   look for an element carrying this file's data attribute and never find one. */
.nx-launcher__toolbar :deep(.nx-launcher__settings) {
  margin-left: auto;
}

.nx-launcher__settings-body {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-md);
}

.nx-launcher__settings-group {
  margin: 0 0 var(--nxt-space-xs);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-subtle);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* Label wraps the switch, so the caption is part of the hit area. */
.nx-launcher__switch-row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  cursor: pointer;
}

/* Group — the outer disclosure. Radius is one step above the section's so the
   nested corners do not look pinched (design-guidelines §4). */
.nx-launcher__group {
  background: var(--nxt-surface-raised-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  box-shadow: var(--nxt-shadow-sm);
  overflow: hidden;
}

/* Own chevron instead of the native disclosure triangle. `list-style: none` drops
   it in current engines, the vendor pseudo covers older WebKit. Flex is safe here
   precisely BECAUSE the native marker is gone — it would otherwise swallow it. */
.nx-launcher__group-head,
.nx-launcher__section-head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  cursor: pointer;
  color: var(--nxt-fg);
  list-style: none;
  transition: background var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-launcher__group-head::-webkit-details-marker,
.nx-launcher__section-head::-webkit-details-marker {
  display: none;
}

/* Full strength on BOTH levels, stated rather than inherited: this is the only
   remaining hint that a level opens at all, so it must not dim along with a
   caption someone mutes later — which is exactly how the section chevron once
   ended up paler than the group's. */
.nx-launcher__disclosure {
  flex: none;
  color: var(--nxt-fg);
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}

/* Group glyph. Sits tight behind the chevron (both are leading marks) but keeps a
   full step to the caption, so the pair does not read as one wide symbol.
   Inherits the caption colour — see `.nx-launcher__disclosure`. */
.nx-launcher__group-icon {
  flex: none;
  margin-right: var(--nxt-space-2xs);
}

/* The grip leads the head, quieter than the caption until pointed at. The
   head's own padding shrinks by the grip's width, so the chevron and caption
   barely move against a launcher without grips. */
.nx-launcher__grip {
  display: inline-grid;
  flex: none;
  place-items: center;
  inline-size: var(--nxt-icon-size-xl);
  block-size: var(--nxt-icon-size-xl);
  margin-left: calc(var(--nxt-space-lg) * -1 + var(--nxt-space-2xs));
  margin-right: var(--nxt-space-2xs);
  padding: 0;
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-subtle);
  cursor: grab;
}

.nx-launcher__grip > svg {
  inline-size: var(--nxt-icon-size-md);
  block-size: var(--nxt-icon-size-md);
}

.nx-launcher__grip:hover {
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
}

.nx-launcher__grip:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

.nx-launcher__grip[aria-pressed='true'] {
  color: var(--nxt-brand-bg);
  cursor: grabbing;
}

/* The dragged group follows the pointer, lifted above the others. */
.nx-launcher__group[data-nx-sortable-state='pointer'] {
  box-shadow: var(--nxt-shadow-lg);
  border-color: color-mix(in srgb, var(--nxt-brand-bg) 45%, var(--nxt-border));
  cursor: grabbing;
}

/* Keyboard-lifted: stays put, a dashed brand frame says „in the hand“. */
.nx-launcher__group[data-nx-sortable-state='keyboard'] {
  outline: calc(var(--nxt-hairline) * 2) dashed var(--nxt-brand-bg);
  outline-offset: calc(var(--nxt-hairline) * 2);
}

/* Landing slot: a brand bar along the edge the group lands at. Inset, because
   the card clips everything outside its frame (`overflow: hidden`); on the
   head (or the open body at the bottom) rather than the card, because an inset
   shadow sits above its own element's fill — on the card the head's hover fill
   covered it. */
.nx-launcher__group[data-nx-drop-side='before'] > .nx-launcher__group-head {
  box-shadow: inset 0 calc(var(--nxt-hairline) * 3) 0 var(--nxt-brand-bg);
}

.nx-launcher__group[data-nx-drop-side='after']:not([open]) > .nx-launcher__group-head,
.nx-launcher__group[data-nx-drop-side='after'][open] > .nx-launcher__group-body {
  box-shadow: inset 0 calc(var(--nxt-hairline) * -3) 0 var(--nxt-brand-bg);
}

/* Points right when closed, down when open — the direction carries the state. */
details[open] > .nx-launcher__group-head > .nx-launcher__disclosure,
details[open] > .nx-launcher__section-head > .nx-launcher__disclosure {
  transform: rotate(90deg);
}

/* Top of a three-step ladder: 18 / 14 / 14px with the tile at regular weight.
   Every size is stated, none inherited — the group caption used to fall through
   to the body size, which put it level with the tile labels underneath it. */
.nx-launcher__group-head {
  padding: var(--nxt-space-sm) var(--nxt-space-lg);
  font-size: var(--nxt-font-size-lg);
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-launcher__group-head:hover,
.nx-launcher__section-head:hover {
  background: var(--nxt-surface-sunken-bg);
}

.nx-launcher__group-head:focus-visible,
.nx-launcher__section-head:focus-visible,
.nx-launcher__item:focus-visible {
  outline: var(--nxt-focus-ring);
  outline-offset: 2px;
}

/* Fixed, not relative: as an `em` it scaled with whichever caption carried it, so
   the same counter came out at two different sizes on the two levels. */
.nx-launcher__count {
  margin-left: var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  color: var(--nxt-fg-muted);
}

.nx-launcher__group-body {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  padding: 0 var(--nxt-space-lg) var(--nxt-space-md);
}

/* Section — quieter than the group: no own surface, just a rule and a caption.
   Written like the group caption — same case, same weight, just smaller. Size,
   the softer colour and the hairline below carry the level difference; an
   uppercase-and-tracked treatment made the quieter level look like a different
   kind of thing altogether. The chevron keeps full strength (see
   `.nx-launcher__disclosure`), so the softer tone stays a heading cue and never
   reads as a disabled control. */
.nx-launcher__section-head {
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-subtle);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
}

/* `font-size: inherit` against the kit's global `ul` reset, which sets 16px. */
.nx-launcher__items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr));
  gap: var(--nxt-space-sm);
  margin: 0;
  padding: var(--nxt-space-sm) 0 0;
  font-size: inherit;
  list-style: none;
}

/* Tile — neutral by default; brand appears only on hover/focus, so colour stays
   in the ~10% reserved for interaction (design-guidelines §3, 60-30-10).
   `width` and the font reset matter for the button form of a tile. */
.nx-launcher__item {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: calc(var(--nxt-size-unit) * 3.25);
  padding: var(--nxt-space-xs) var(--nxt-space-md);
  font-family: inherit;
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  background: var(--nxt-surface-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  transition:
    transform var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-base) var(--nxt-ease-out);
}

/* Caption takes the free space, badge stays put at the trailing edge, so the
   badges line up across a row of tiles regardless of caption length. */
.nx-launcher__item-label {
  flex: 1;
  min-width: 0;
}

.nx-launcher__item-badge {
  flex: none;
  margin-left: var(--nxt-space-sm);
}

/* Search hit — a brand tint behind unchanged text, plus weight as the second
   signal colour must never carry alone (§3). `--nxt-brand-border` (#75a5de light,
   #2a588c dark in the navy brand) is the one mid-tone that stands against the
   #ffffff / #16171a tile in both themes. Using a border token as a fill is a
   stretch, but the token system has no highlight role — every candidate borrows
   one, and brand at least means "active state".
   The colour must NOT sit on the text: a hover recolouring the whole caption made
   the mark vanish exactly while pointing at it. Hence a background, and
   `color: var(--nxt-fg)` to pin the glyphs. No padding either — it pulls the
   letters apart ("Plan gr uppen").
   Rejected, so nobody walks the same line again:
   `--nxt-brand-subtle-bg` (#f7faff / #0e1114) and its `-hover` step are invisible
   against the tile — the subtle family is for large calm areas, not a few glyphs.
   `--nxt-brand-bg` + `--nxt-fg-on-accent` inverts fore- AND background mid-word.
   `--nxt-link-fg` as text colour collides with a hover recolour (see above).
   `--nxt-warning-border` (#d78d65 / #844219) came closest to a yellow marker but
   reads as a warning, which a search hit is not — tried and dropped. */
.nx-launcher__item mark {
  font-weight: 700;
  color: var(--nxt-fg);
  background: var(--nxt-brand-border);
}

/* Lift, border and a brand-tinted shadow — the same as `.cx-home-app` in
   connect/src/views/HomeView.vue so a tile behaves the same wherever one appears.
   Deliberately no text recolour on hover: the home tiles do not do it either, and
   it was what made the search mark hard to spot while pointing at a tile. */
.nx-launcher__item:hover:not(.is-disabled) {
  border-color: var(--nxt-brand-bg);
  transform: translateY(-2px);
  box-shadow: 0 10px 28px color-mix(in srgb, var(--nxt-brand-bg) 14%, transparent);
}

.nx-launcher__item:active:not(.is-disabled) {
  transform: translateY(-1px);
}

.nx-launcher__item.is-disabled {
  color: var(--nxt-fg-muted);
  cursor: not-allowed;
}

@media (prefers-reduced-motion: reduce) {
  .nx-launcher__group-head,
  .nx-launcher__section-head,
  .nx-launcher__disclosure,
  .nx-launcher__item {
    transition: none;
  }

  /* Killing only the transition would leave the lift as an instant jump, which is
     exactly the motion the setting asks us to avoid. Same selectors as the lift
     itself, so this rule is as specific and wins by coming later. */
  .nx-launcher__item:hover:not(.is-disabled),
  .nx-launcher__item:active:not(.is-disabled) {
    transform: none;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-launcher__group-head,
:where([data-nx-motion='reduced']) .nx-launcher__section-head,
:where([data-nx-motion='reduced']) .nx-launcher__disclosure,
:where([data-nx-motion='reduced']) .nx-launcher__item {
  transition: none;
}
:where([data-nx-motion='reduced']) .nx-launcher__item:hover:not(.is-disabled),
:where([data-nx-motion='reduced']) .nx-launcher__item:active:not(.is-disabled) {
  transform: none;
}
</style>
